<template>
  <div class="is-flex is-flex-direction-column">
    <textarea
      style="width: 100%"
      class="textarea is-family-monospace"
      :class="{ 'is-danger': textAreaErrors.length >= 1 }"
      :placeholder="textAreaPlaceholder"
      :value="textAreaSamplesString"
      @input="(e) => (textAreaSamplesString = e.target.value)"
    />
    <ul v-if="textAreaErrors.length >= 1" class="is-size-7 has-text-danger p-1">
      <li v-for="e of textAreaErrors" :key="`err-line${e.line}`">
        Line {{ e.line }}: {{ e.message }}
      </li>
    </ul>
  </div>
</template>

<script>
import { ref, computed, reactive, watch } from 'vue'
import { capitalize } from 'lodash-es'
import { helpers } from '@vuelidate/validators'
import { useVuelidate } from '@vuelidate/core'

import { getVuelidateValidationRules } from '@/composables/fields/useFieldValidation'

import { useSample } from '@/composables/useSample'

export default {
  name: 'CreateSamplesCsv',
  props: {
    sampleTypeId: {
      type: String,
      default: 'genericsample',
    },
  },
  setup(props, { emit }) {
    const sampleTypeId = ref('')
    watch(
      () => props.sampleTypeId,
      (v) => (sampleTypeId.value = v),
      { immediate: true },
    )
    const { sampleFieldsNames, sampleFieldsModels } = useSample(
      sampleTypeId,
      [],
    )

    const textAreaSamplesString = ref('')

    /* Simple sample object, without model meta nesting, used for validation
     * The field values are the string values from the textarea form field */
    const simpleSamples = computed(() => {
      let textAreaSamples = []
      if (textAreaSamplesString.value) {
        textAreaSamples = textAreaSamplesString.value.trim().split('\n')
        textAreaSamples = textAreaSamples.map((line) => {
          const values = line.split(',').map((v) => v.trim())
          return sampleFieldsNames.value.reduce(
            (sample, fieldName, fieldIndex) => ({
              ...sample,
              [fieldName]: values[fieldIndex] || '',
            }),
            {},
          )
        })
      }
      return textAreaSamples
    })

    /* Validation samples */
    const validationRules = computed(() =>
      sampleFieldsNames.value.reduce((validationRules, fieldName) => {
        let validations = getVuelidateValidationRules(
          fieldName,
          sampleFieldsModels.value[fieldName]?.validations || [],
        )
        // next reduce will be unecessary with global field label handling
        // from within field component
        validations = Object.entries(validations).reduce(
          (validations, [vId, validator]) => {
            if (vId === 'required')
              validator = helpers.withMessage(
                `${capitalize(fieldName)} is mandatory`,
                validator,
              )
            return {
              ...validations,
              [vId]: validator,
            }
          },
          {},
        )
        return {
          ...validationRules,
          [fieldName]: validations,
        }
      }, {}),
    )
    const v$Samples = useVuelidate(
      { samples: { $each: helpers.forEach(validationRules.value) } },
      reactive({ samples: simpleSamples }),
    )
    watch(textAreaSamplesString, () => v$Samples?.value?.$touch(), {
      immediate: true,
    })

    const textAreaErrors = computed(() => {
      let allErrors =
        (v$Samples.value.$error && v$Samples.value.$errors[0]) || {}
      allErrors = allErrors?.$message?.reduce((errors, e, eIndex) => {
        if (e.length >= 1) {
          errors.push({
            line: eIndex + 1,
            message: e.join(', '),
          })
        }
        return errors
      }, [])
      return allErrors || []
    })

    /* Final samples */
    const finalSamples = computed(() => {
      let samples = []
      if (textAreaErrors.value.length === 0) {
        samples = simpleSamples.value.map((s) => {
          return Object.entries(s).reduce((sample, [fieldName, fieldValue]) => {
            if (fieldName === 'name')
              fieldValue = { value: { name: fieldValue } }
            else fieldValue = { value: fieldValue }
            return {
              ...sample,
              [fieldName]: fieldValue,
            }
          }, {})
        })
      }
      return samples
    })
    watch(finalSamples, (s) => emit('new-samples', s?.length >= 1 ? s : null))

    return {
      sampleFieldsNames,
      v$Samples,
      textAreaSamplesString,
      textAreaErrors,
      simpleSamples,
      validationRules,
    }
  },
  computed: {
    textAreaPlaceholder() {
      return this.sampleFieldsNames
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(',')
    },
  },
}
</script>
